import React, { useImperativeHandle, forwardRef, useEffect, useMemo } from 'react';
import Script from 'next/script';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import url from '@/utils/config';
import { PREFECTURES } from '@/utils/prefectures';
import { CollectionAddressType } from '@/mobile/types/api/mypage/collectionAddress';
import agreementCheckState from '@/mobile/states/rentalwifi/agreementCheckState';
import selectedCollectionAddressState from '@/mobile/states/rentalwifi/selectedCollectionAddressState';
import { toZenkaku, isValidPhoneNumber, isNotBlankCharOnly } from '@/utils/str';

/**
 * 回収先住所入力フォームコンポーネント
 * レンタルWiFi機器の返却先住所を入力するフォームを提供する。
 * 郵便番号からの住所自動入力やバリデーションを含む。
 */
type CollectionAddressFormProps = {
  showValidation?: boolean;
};

// 外部から参照可能な関数の型定義
export interface CollectionAddressFormRef {
  validateForm: (shouldResetAgreement?: boolean) => boolean;
  getValidationErrors: () => string[];
  isCurrentlyValid: () => boolean;
  getCurrentValues: () => CollectionAddressType;
}

// ========================================================================
// バリデーションスキーマ
// ========================================================================

const schema = yup.object().shape({
  zipCode: yup
    .string()
    .required('郵便番号を入力してください。')
    .matches(/^\d{7}$/, '郵便番号は半角数字7桁で入力してください。'),
  prefecture: yup.string().required('都道府県を選択してください。'),
  city: yup
    .string()
    .required('市区町村を入力してください。')
    .test('isValidCity', '不正な市区町村名です。', isNotBlankCharOnly),
  district: yup
    .string()
    .required('丁目を入力してください。')
    .test('isValidDistrict', '不正な丁目名です。', isNotBlankCharOnly),
  street: yup
    .string()
    .required('番地を入力してください。')
    .test('isValidStreet', '不正な番地名です。', isNotBlankCharOnly),
  unit: yup.string(),
  subUnit: yup.string(),
  building: yup.string(),
  room: yup.string(),
  phone: yup
    .string()
    .required('電話番号を入力してください。')
    .test('isValidPhone', '不正な電話番号です。', isNotBlankCharOnly)
    .matches(/^\d+$/, '電話番号は半角数字で入力してください。')
    .test('isValidPhone', '不正な電話番号です。', isValidPhoneNumber),
});

// ========================================================================
// メインコンポーネント
// ========================================================================

const CollectionAddressForm = forwardRef<CollectionAddressFormRef, CollectionAddressFormProps>(
  ({ showValidation = true }, ref) => {
    // 入力変更で確認条件が崩れるため、親画面の同意状態を必要に応じて落とせるようにする。
    const setAgreementCheck = useSetRecoilState(agreementCheckState);

    // 画面戻り時に手入力住所を復元できるよう、Recoil から初期値を再構築する。
    const selectedData = useRecoilValue(selectedCollectionAddressState);
    const customAddress = useMemo(() => {
      return typeof selectedData.address === 'object' && selectedData.address !== null
        ? (selectedData.address as CollectionAddressType)
        : {
            zipCode: '',
            prefecture: '',
            city: '',
            district: '',
            street: '',
            unit: '',
            subUnit: '',
            building: '',
            room: '',
            phone: '',
          };
    }, [selectedData.address]);

    // バリデーションと表示制御を一元化するため、フォーム管理は react-hook-form に寄せる。
    const {
      register,
      setValue,
      trigger,
      formState: { errors, isValid },
      getValues,
      reset,
    } = useForm<CollectionAddressType>({
      resolver: yupResolver(schema),
      mode: 'onChange',
      defaultValues: customAddress, // Recoil stateから初期値を設定
    });

    // 手入力途中で確認画面へ進んだあと戻ってきても再入力不要にする。
    useEffect(() => {
      if (customAddress && Object.values(customAddress).some((value) => value !== '')) {
        reset(customAddress);
      }
    }, [customAddress, reset]);

    // ====================================================================
    // AjaxZip3関連の処理
    // ====================================================================

    // AjaxZip3 が書き換えた hidden 値を react-hook-form 側へ反映し、単一の状態源に戻す。
    const handleAjaxZip3Success = () => {
      // 隠しフィールドから値を取得（AjaxZip3がこれらを更新する）
      const hiddenPref = document.getElementsByName('collectionPrefecture')[0] as HTMLSelectElement;
      const hiddenCity = document.getElementsByName('collectionCity')[0] as HTMLInputElement;
      const hiddenDistrict = document.getElementsByName(
        'collectionDistrict'
      )[0] as HTMLInputElement;
      const hiddenBlock = document.getElementsByName('collectionBlock')[0] as HTMLInputElement;

      // 住所自動入力後にフォーム値を更新し、手入力欄と検証結果を同期させる。
      if (hiddenPref && hiddenPref.value) {
        setValue('prefecture', hiddenPref.value);
      }

      if (hiddenCity && hiddenCity.value && hiddenDistrict && hiddenDistrict.value) {
        const fullAddress = hiddenCity.value;
        const area = hiddenDistrict.value;
        const prefName = hiddenPref ? hiddenPref.options[hiddenPref.selectedIndex]?.text || '' : '';

        // 市区町村 = 全体の住所 - 都道府県 - 町域
        let city = fullAddress;
        if (prefName) {
          city = city.replace(prefName, '');
        }
        city = city.replace(area, '').trim();

        setValue('city', city);
        setValue('district', area);
      }

      // 番地を設定
      if (hiddenBlock && hiddenBlock.value) {
        setValue('street', hiddenBlock.value);
      }

      // バリデーションを再実行
      trigger();
    };

    // AjaxZip3 は name 属性付き DOM を前提にしているため、送信前に hidden 値を同期する。
    const updateHiddenFields = () => {
      const values = getValues();
      const hiddenZip = document.getElementsByName('collectionZipCode')[0] as HTMLInputElement;
      const hiddenPref = document.getElementsByName('collectionPrefecture')[0] as HTMLSelectElement;
      const hiddenCity = document.getElementsByName('collectionCity')[0] as HTMLInputElement;
      const hiddenDistrict = document.getElementsByName(
        'collectionDistrict'
      )[0] as HTMLInputElement;
      const hiddenBlock = document.getElementsByName('collectionBlock')[0] as HTMLInputElement;

      if (hiddenZip) hiddenZip.value = values.zipCode || '';
      if (hiddenPref) hiddenPref.value = values.prefecture || '';
      if (hiddenCity) hiddenCity.value = values.city || '';
      if (hiddenDistrict) hiddenDistrict.value = values.district || '';
      if (hiddenBlock) hiddenBlock.value = values.street || '';
    };

    // 既存ライブラリ要件を満たすためだけの hidden 領域で、画面表示には使わない。
    const renderHiddenFieldsForAjaxZip3 = () => {
      const values = getValues();
      return (
        <div style={{ display: 'none' }}>
          <input name='collectionZipCode' defaultValue={values.zipCode || ''} />
          <select name='collectionPrefecture' defaultValue={values.prefecture || ''}>
            <option value=''>選択してください</option>
            {PREFECTURES.map((prefecture) => (
              <option key={prefecture.value} value={prefecture.value}>
                {prefecture.label}
              </option>
            ))}
          </select>
          <input name='collectionCity' defaultValue={values.city || ''} />
          <input name='collectionDistrict' defaultValue={values.district || ''} />
          <input name='collectionBlock' defaultValue={values.street || ''} />
        </div>
      );
    };

    // ====================================================================
    // イベントハンドラー
    // ====================================================================

    // 郵便番号妥当性を最低限確認してから外部ライブラリへ処理を渡す。
    const handleAutoInputClick = async (e: React.MouseEvent | React.KeyboardEvent) => {
      // キーボードのタブ移動時のクリック挙動を防止する
      if ('key' in e && e.key !== 'Enter') {
        return;
      }

      const currentValues = getValues();
      const { zipCode } = currentValues;

      if (!zipCode) {
        // eslint-disable-next-line no-alert
        alert('郵便番号を入力してください');
        return;
      }

      if (zipCode.length !== 7 || !/^\d{7}$/.test(zipCode)) {
        // eslint-disable-next-line no-alert
        alert('郵便番号は半角数字7桁で入力してください');
        return;
      }

      // 検索前に hidden 値を最新化しないと AjaxZip3 が古い入力を参照してしまう。
      updateHiddenFields();

      // 郵便番号から住所候補を引き、結果を success/failure ハンドラで取り込む。
      (window as any).AjaxZip3.zip2addr(
        'collectionZipCode',
        '',
        'collectionPrefecture',
        'collectionCity',
        'collectionDistrict',
        'collectionBlock'
      );

      (window as any).AjaxZip3.onSuccess = () => {
        setTimeout(() => {
          handleAjaxZip3Success();
        }, 100); // DOM更新を待つ
      };

      (window as any).AjaxZip3.onFailure = () => {
        // eslint-disable-next-line no-alert
        alert('郵便番号から住所の取得に失敗しました');
      };
    };

    // 住所系項目は表記ゆれを抑えるため、blur 時に全角へ寄せる。
    const handleBlurWithZenkaku =
      (field: keyof CollectionAddressType) => (e: React.FocusEvent<HTMLInputElement>) => {
        const zenkakuValue = toZenkaku(e.target.value);
        if (zenkakuValue !== e.target.value) {
          setValue(field, zenkakuValue);
        }
      };

    // ====================================================================
    // 外部アクセス用の関数
    // ====================================================================

    const validateForm = (shouldResetAgreement: boolean = true): boolean => {
      trigger(); // バリデーションを実行

      // 住所入力が崩れたまま同意だけ残る状態を防ぐため、必要時は同意を落とす。
      if (!isValid && shouldResetAgreement) {
        setAgreementCheck(false);
      }

      return isValid;
    };

    const getValidationErrors = (): string[] => {
      return Object.values(errors).map((error) => error?.message || '');
    };

    const isCurrentlyValid = (): boolean => {
      return isValid;
    };

    const getCurrentValues = (): CollectionAddressType => {
      const currentValues = getValues();
      return currentValues;
    };

    // ====================================================================
    // useImperativeHandle
    // ====================================================================

    // 親コンポーネントから送信判定や値取得を行えるように、命令型 API を公開する。
    useImperativeHandle(ref, () => ({
      validateForm,
      getValidationErrors,
      isCurrentlyValid,
      getCurrentValues,
    }));

    return (
      <div>
        <Script src={url('/utils/ajaxzip3.js')} strategy='lazyOnload' />

        {/* AjaxZip3用の隠しフィールド */}
        {renderHiddenFieldsForAjaxZip3()}

        <div className='collection-form__container'>
          {/* 郵便番号セクション */}
          <div className='subsubsection--mt0'>
            <h4 className='title3'>郵便番号</h4>
            <div className='collection-form__postcode-container'>
              <div className='collection-form__postcode-field'>
                <input
                  className={`collection-form__postcode-input ${
                    showValidation && errors.zipCode ? 'collection-form__input--error' : ''
                  }`}
                  placeholder='例：1234567'
                  maxLength={7}
                  type='tel'
                  {...register('zipCode')}
                />
                {showValidation && errors.zipCode && (
                  <span className='collection-form__error'>{errors.zipCode.message}</span>
                )}
              </div>
              <div className='postcode__btnCol'>
                <button
                  type='button'
                  className='postcode__btn collection-form__auto-input-btn'
                  onClick={handleAutoInputClick}
                  onKeyDown={handleAutoInputClick}
                >
                  住所自動入力
                </button>
              </div>
            </div>
          </div>

          {/* 住所セクション */}
          <div className='subsubsection collection-form__address-section'>
            <h4 className='title3'>住所</h4>

            {/* 都道府県 */}
            <div className='collection-form__field-container'>
              <select
                className={`collection-form__select ${
                  showValidation && errors.prefecture ? 'collection-form__select--error' : ''
                }`}
                {...register('prefecture')}
              >
                {PREFECTURES.map((prefecture) => (
                  <option key={prefecture.value} value={prefecture.value}>
                    {prefecture.label}
                  </option>
                ))}
              </select>
              {showValidation && errors.prefecture && (
                <span className='collection-form__error'>{errors.prefecture.message}</span>
              )}
            </div>

            {/* 市区町村 */}
            <div className='collection-form__field-container'>
              <label htmlFor='collectionCity'>市区町村</label>
              <input
                id='collectionCity'
                maxLength={20}
                type='text'
                placeholder='例：品川区'
                className={`collection-form__input--full-width ${
                  showValidation && errors.city ? 'collection-form__input--error' : ''
                }`}
                {...register('city')}
                onBlur={handleBlurWithZenkaku('city')}
              />
              {showValidation && errors.city && (
                <span className='collection-form__error'>{errors.city.message}</span>
              )}
            </div>

            {/* 丁目など */}
            <div className='collection-form__field-container'>
              <label htmlFor='collectionDistrict'>丁目など</label>
              <input
                id='collectionDistrict'
                maxLength={20}
                type='text'
                placeholder='例：1丁目、1'
                className={`collection-form__input--full-width ${
                  showValidation && errors.district ? 'collection-form__input--error' : ''
                }`}
                {...register('district')}
                onBlur={handleBlurWithZenkaku('district')}
              />
              {showValidation && errors.district && (
                <span className='collection-form__error'>{errors.district.message}</span>
              )}
            </div>

            {/* 番地 */}
            <div className='collection-form__field-container'>
              <label htmlFor='collectionBlock'>番地</label>
              <input
                id='collectionBlock'
                maxLength={5}
                type='text'
                placeholder='例：2番地、2番、2'
                className={`collection-form__input--auto-width ${
                  showValidation && errors.street ? 'collection-form__input--error' : ''
                }`}
                {...register('street')}
                onBlur={handleBlurWithZenkaku('street')}
              />
              {showValidation && errors.street && (
                <span className='collection-form__error'>{errors.street.message}</span>
              )}
            </div>

            {/* 号 */}
            <div className='collection-form__field-container'>
              <label htmlFor='collectionUnit'>号</label>
              <input
                id='collectionUnit'
                maxLength={5}
                type='text'
                placeholder='例：3号、3'
                className='collection-form__input--auto-width'
                {...register('unit')}
                onBlur={handleBlurWithZenkaku('unit')}
              />
            </div>

            {/* 枝番 */}
            <div className='collection-form__field-container'>
              <label htmlFor='collectionSubUnit'>枝番</label>
              <input
                id='collectionSubUnit'
                maxLength={5}
                type='text'
                placeholder='例：123'
                className='collection-form__input--auto-width'
                {...register('subUnit')}
                onBlur={handleBlurWithZenkaku('subUnit')}
              />
            </div>
          </div>

          {/* 建物情報セクション */}
          <div className='subsubsection collection-form__address-section'>
            <h4 className='title3'>建物情報</h4>

            {/* マンション・アパート名 */}
            <div className='collection-form__field-container'>
              <label htmlFor='collectionBuilding'>マンション・アパート名</label>
              <input
                id='collectionBuilding'
                maxLength={25}
                type='text'
                placeholder='例：品川シーサイド TSタワー'
                className='collection-form__input--full-width'
                {...register('building')}
                onBlur={handleBlurWithZenkaku('building')}
              />
            </div>

            {/* 部屋番号 */}
            <div className='collection-form__field-container'>
              <label htmlFor='collectionRoom'>部屋番号</label>
              <input
                id='collectionRoom'
                maxLength={20}
                type='text'
                placeholder='例：101'
                className='collection-form__input--auto-width'
                {...register('room')}
                onBlur={handleBlurWithZenkaku('room')}
              />
            </div>
          </div>

          {/* 電話番号セクション */}
          <div className='subsubsection collection-form__telephone-section'>
            <h4 className='title3'>電話番号</h4>
            <div className='collection-form__field-container'>
              <input
                maxLength={11}
                type='tel'
                placeholder='例：09012345678'
                className={`collection-form__input--auto-width ${
                  showValidation && errors.phone ? 'collection-form__input--error' : ''
                }`}
                {...register('phone')}
              />
              {showValidation && errors.phone && (
                <span className='collection-form__error'>{errors.phone.message}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

// displayNameを設定
CollectionAddressForm.displayName = 'CollectionAddressForm';

export default CollectionAddressForm;
